import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { debounce } from "lodash";
import partnerApiKeyService from "../../services/partner-api-key.service";
import TransferInRequestService from "../../services/transfer-in-request.service";
import TransferOutRequestService from "../../services/transfer-out-request.service";
import ViewTransferRequestModal from "../../components/ViewTransferRequestModal";
import { Pagination } from "../../components/Pagination";
import { ROUTES } from "../../constants/routes.constants";
import "./index.scss";

const TABS = [
  { id: "patients", label: "Patients" },
  { id: "users", label: "Users" },
  { id: "prescriptions", label: "Prescriptions" },
];

const PartnerSoftwareDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.currentUser);
  const apiKeyId = Number(id);

  const [apiKey, setApiKey] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("patients");
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [viewTransferModalShow, setViewTransferModalShow] = useState(false);
  const [transferRequest, setTransferRequest] = useState(null);
  const [transferRequestType, setTransferRequestType] = useState(0);
  const limit = 50;

  /** Same-branch transfers are typically transfer-out; different branches → transfer-in. */
  const inferTransferRequestType = (row) =>
    row.fromPharmacyBranchId === row.toPharmacyBranchId ? 1 : 0;

  const openTransferView = async (row) => {
    const requestId = row.requestId;
    const requesttype = inferTransferRequestType(row);
    const service =
      requesttype === 1 ? TransferOutRequestService : TransferInRequestService;
    let result = await service.GetById(requestId);
    if (result?.error) {
      const fallback =
        requesttype === 1 ? TransferInRequestService : TransferOutRequestService;
      result = await fallback.GetById(requestId);
      if (result?.error === null) {
        setTransferRequestType(requesttype === 1 ? 0 : 1);
      }
    } else {
      setTransferRequestType(requesttype);
    }
    if (result?.error === null) {
      setTransferRequest(result.data);
      setViewTransferModalShow(true);
    }
  };

  const loadApiKey = useCallback(async () => {
    const result = await partnerApiKeyService.GetById(apiKeyId);
    if (result.success) {
      setApiKey(result.data);
    }
  }, [apiKeyId]);

  const loadStats = useCallback(async () => {
    const result = await partnerApiKeyService.GetAttributionStats(apiKeyId);
    if (result.success) {
      setStats(result.data);
    }
  }, [apiKeyId]);

  const loadTabData = useCallback(
    async (tab, pageNum, q) => {
      const params = { page: pageNum, limit, q: q || undefined };
      let result;
      if (tab === "patients") {
        result = await partnerApiKeyService.ListPatients(apiKeyId, params);
      } else if (tab === "users") {
        result = await partnerApiKeyService.ListUsers(apiKeyId, params);
      } else {
        result = await partnerApiKeyService.ListPrescriptions(apiKeyId, params);
      }
      if (result.success) {
        const list = result.data?.data;
        setRecords(Array.isArray(list) ? list : []);
        setTotal(Number(result.data?.total) || 0);
      } else {
        setRecords([]);
        setTotal(0);
      }
    },
    [apiKeyId, limit]
  );

  const debouncedSearch = debounce((keyword) => {
    setSearchText(keyword);
    setPage(1);
  }, 300);

  useEffect(() => {
    if (currentUser?.user?.roleId !== 1) return;
    document.title = "TransferMyRx::Partner Platform Detail";
    const pageTitle = document.getElementById("page-title-text");
    if (pageTitle) {
      pageTitle.innerText = "Partner Platform";
    }
    if (!isNaN(apiKeyId)) {
      loadApiKey();
      loadStats();
    }
  }, [apiKeyId, currentUser, loadApiKey, loadStats]);

  useEffect(() => {
    if (!isNaN(apiKeyId)) {
      loadTabData(activeTab, page, searchText);
    }
  }, [activeTab, page, apiKeyId, loadTabData, searchText]);

  const getTabBadgeCount = (tabId) => {
    if (!stats?.attributionEnabled) return null;
    if (tabId === "patients") return stats.patients;
    if (tabId === "users") return stats.users;
    if (tabId === "prescriptions") return stats.prescriptions;
    return null;
  };

  if (currentUser?.user?.roleId !== 1) {
    return <>No Access</>;
  }

  if (isNaN(apiKeyId)) {
    return <p className="text-muted">Invalid platform ID.</p>;
  }

  const renderPatientsTable = () => (
    <table className="table mb-0">
      <thead>
        <tr>
          <th className="text-center">ID</th>
          <th>Name</th>
          <th>Email</th>
          <th>Mobile</th>
          <th className="text-center">DOB</th>
          <th className="text-center">Created</th>
        </tr>
      </thead>
      <tbody>
        {(Array.isArray(records) ? records : []).map((row) => (
          <tr key={row.id}>
            <td className="text-center">{row.id}</td>
            <td>
              {row.firstName} {row.lastName}
            </td>
            <td>{row.emailAddress || "—"}</td>
            <td>{row.mobile || "—"}</td>
            <td className="text-center">
              {row.dateOfBirth
                ? moment(row.dateOfBirth).format("MM/DD/YYYY")
                : "—"}
            </td>
            <td className="text-center">
              {row.createdOn
                ? moment(row.createdOn).format("MM/DD/YY HH:mm")
                : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderUsersTable = () => (
    <table className="table mb-0">
      <thead>
        <tr>
          <th className="text-center">ID</th>
          <th>Name</th>
          <th>Email</th>
          <th>Mobile</th>
          <th className="text-center">Role</th>
          <th className="text-center">Created</th>
        </tr>
      </thead>
      <tbody>
        {(Array.isArray(records) ? records : []).map((row) => (
          <tr key={row.id}>
            <td className="text-center">{row.id}</td>
            <td>
              {row.firstName} {row.lastName}
            </td>
            <td>{row.emailAddress || "—"}</td>
            <td>{row.mobile || "—"}</td>
            <td className="text-center">{row.roleName || row.roleId || "—"}</td>
            <td className="text-center">
              {row.createdOn
                ? moment(row.createdOn).format("MM/DD/YY HH:mm")
                : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderPrescriptionsTable = () => (
    <table className="table mb-0">
      <thead>
        <tr>
          <th className="text-center">Request #</th>
          <th className="text-center">Rx #</th>
          <th>Patient</th>
          <th className="text-center">Status</th>
          <th className="text-center">From branch</th>
          <th className="text-center">To branch</th>
          <th className="text-center">Created</th>
          <th className="text-center">Actions</th>
        </tr>
      </thead>
      <tbody>
        {(Array.isArray(records) ? records : []).map((row) => (
          <tr key={row.requestId}>
            <td className="text-center">{row.requestId}</td>
            <td className="text-center">{row.rxNumber || "—"}</td>
            <td>
              {row.patientName?.trim() || row.patientId || "—"}
            </td>
            <td className="text-center">{row.statusName || row.statusId}</td>
            <td className="text-center" title={row.fromPharmacyBranchId}>
              {row.fromPharmacyBranchName || row.fromPharmacyBranchId || "—"}
            </td>
            <td className="text-center" title={row.toPharmacyBranchId}>
              {row.toPharmacyBranchName || row.toPharmacyBranchId || "—"}
            </td>
            <td className="text-center">
              {row.createdOn
                ? moment(row.createdOn).format("MM/DD/YY HH:mm")
                : "—"}
            </td>
            <td>
              <div className="d-flex justify-content-center">
                <a
                  href="#"
                  className="view"
                  title="View prescription"
                  onClick={(e) => {
                    e.preventDefault();
                    openTransferView(row);
                  }}
                >
                  <i className="fa fa-eye"></i>
                </a>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const emptyLabel =
    activeTab === "patients"
      ? "patients"
      : activeTab === "users"
        ? "users"
        : "prescriptions";

  return (
    <div className="partner-software-detail">
      <div className="mb-3">
        <Link to={ROUTES.PARTNER_PLATFORM_ROUTE} className="btn btn-link ps-0">
          <i className="fa fa-arrow-left me-1"></i>Back to Partner Platform
        </Link>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <h4 className="mb-1">{apiKey?.clientName || "Loading…"}</h4>
              <p className="text-muted mb-0">
                Key prefix: <code>{apiKey?.prefix}</code>
                {apiKey?.clientEmail && (
                  <>
                    {" "}
                    · {apiKey.clientEmail}
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(ROUTES.PARTNER_PLATFORM_ROUTE)}
            >
              Edit settings
            </button>
          </div>

          {stats && !stats.attributionEnabled && (
            <div className="alert alert-warning mt-3 mb-0">
              Partner attribution is disabled on the server (
              <code>PARTNER_API_ATTRIBUTION_ENABLED</code>). Enable it to track
              which records were created through this API key.
            </div>
          )}

          <div className="row g-3 mt-2">
            {stats?.attributionEnabled && (
              <>
                <div className="col-md-3">
                  <div className="stat-tile">
                    <div className="stat-value">{stats.patients}</div>
                    <div className="stat-label">Patients</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="stat-tile">
                    <div className="stat-value">{stats.users}</div>
                    <div className="stat-label">Users</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="stat-tile">
                    <div className="stat-value">{stats.prescriptions}</div>
                    <div className="stat-label">Prescriptions</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            {TABS.map((tab) => (
              <li className="nav-item" key={tab.id}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setPage(1);
                    setSearchText("");
                  }}
                >
                  {tab.label}
                  {getTabBadgeCount(tab.id) != null && (
                    <span className="badge bg-secondary ms-2">
                      {getTabBadgeCount(tab.id)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-header d-flex justify-content-end bg-white border-top-0 pt-0">
          <div className="search-box">
            <i className="fa fa-search"></i>
            <form onSubmit={(e) => e.preventDefault()}>
              <input
                type="text"
                className="form-control rounded-pill"
                placeholder="Search…"
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </form>
          </div>
        </div>

        <div className="card-body datagrid p-0">
          {!Array.isArray(records) || records.length === 0 ? (
            <p className="text-center text-muted py-4 mb-0">
              No {emptyLabel} attributed to this platform yet.
            </p>
          ) : activeTab === "patients" ? (
            renderPatientsTable()
          ) : activeTab === "users" ? (
            renderUsersTable()
          ) : (
            renderPrescriptionsTable()
          )}
        </div>

        <Pagination
          page={page}
          total={total}
          limit={limit}
          setPage={setPage}
          maxPage={20}
        />
      </div>

      <ViewTransferRequestModal
        show={viewTransferModalShow}
        onHide={() => setViewTransferModalShow(false)}
        requesttype={transferRequestType}
        request={transferRequest}
        handleEditTransferRequestModalShow={() => {}}
        handleAcceptTransferRequestModalShow={() => {}}
        handleFinishReviewTransferRequestModalShow={() => {}}
        handleRejectTransferRequestModalShow={() => {}}
        handleCancelTransferRequestModalShow={() => {}}
        handleManualAutoCompleteRequestModalShow={() => {}}
      />
    </div>
  );
};

export default PartnerSoftwareDetailPage;
